<script lang="ts">
import Vue from "vue";
import Component from "vue-class-component";
import DynamicForm from "@/components/Settings/Services/components/DynamicForm.vue";
import { metadataModule } from "@/store/modules/metadata/metadataModule";
import { IGiftCardTypeDTO, IUpsertGiftCardTypeDTO } from "@shared/types";
import { giftCardsModule } from "@/store/modules/giftCards/giftCardsModule";

@Component({
  name: "GiftCardTypesSettings",
  components: { DynamicForm },
})
export default class GiftCardTypesSettings extends Vue {
  public editingType: Partial<IUpsertGiftCardTypeDTO> | null = null;
  public showForm = false;
  public isEditing = false;
  public isCreating = false;

  get defaultType(): Partial<IUpsertGiftCardTypeDTO> {
    return {
      name: "",
      value: 0,
      currency: "NOK",
      durationDays: undefined,
      isActive: true,
    };
  }

  get nonReactiveTranslations() {
    return Object.freeze({ ...this.$ts.dtos.upsertGiftCardTypeDTO });
  }

  async created() {
    await giftCardsModule.fetchTypes();
  }

  createNewType() {
    this.isCreating = true;
    this.isEditing = false;
    this.editingType = { ...this.defaultType };
    this.showForm = true;
  }

  startEdit(type: IGiftCardTypeDTO) {
    this.isEditing = true;
    this.isCreating = false;
    this.editingType = {
      id: type.id,
      name: type.name,
      value: type.value,
      currency: type.currency,
      durationDays: type.durationDays,
      isActive: type.isActive,
    };
    this.showForm = true;
  }

  cancelEdit() {
    this.isEditing = false;
    this.isCreating = false;
    this.editingType = null;
    this.showForm = false;
  }

  async onSave(formData: IUpsertGiftCardTypeDTO) {
    try {
      await giftCardsModule.saveType(formData);
      await giftCardsModule.fetchTypes();
      this.cancelEdit();
    } catch (error) {
      console.error("Error saving gift card type:", error);
    }
  }

  formatCurrency(value: number): string {
    return new Intl.NumberFormat("no-NO", {
      style: "currency",
      currency: "NOK",
    }).format(value);
  }

  get upsertGiftCardTypeMetadata() {
    return metadataModule.upsertGiftCardTypeMetadata;
  }

  get giftCardTypes() {
    return giftCardsModule.types;
  }

  get loading() {
    return giftCardsModule.loading;
  }

  get headers() {
    return [
      { text: this.$t(this.$ts.giftCardManagement.types.name).toString(), value: 'name' },
      { text: this.$t(this.$ts.giftCardManagement.types.value).toString(), value: 'value' },
      { text: this.$t(this.$ts.giftCardManagement.types.currency).toString(), value: 'currency' },
      { text: this.$t(this.$ts.giftCardManagement.types.duration).toString(), value: 'durationDays' },
      { text: this.$t(this.$ts.giftCardManagement.types.status).toString(), value: 'isActive' },
      { text: this.$t(this.$ts.giftCardManagement.types.actions).toString(), value: 'actions', sortable: false },
    ];
  }
}
</script>

<template>
  <div>
    <v-dialog
      v-model="showForm"
      max-width="600"
    >
      <DynamicForm
        v-if="editingType"
        :data="editingType"
        entity="Gift Card Type"
        :metadata="upsertGiftCardTypeMetadata"
        :translation-path="nonReactiveTranslations"
        :is-editing="isEditing"
        :is-creating="isCreating"
        :edit-title="$t($ts.giftCardManagement.types.editTitle)"
        :create-title="$t($ts.giftCardManagement.types.createTitle)"
        @save="onSave"
        @create="onSave"
        @cancel="cancelEdit"
      />
    </v-dialog>

    <v-card flat>
      <v-card-text>
        <v-data-table
          :headers="headers"
          :items="giftCardTypes"
          :loading="loading"
        >
          <template #item.value="{ item }">
            {{ formatCurrency(item.value) }}
          </template>

          <template #item.durationDays="{ item }">
            {{ item.durationDays ? $t($ts.giftCardManagement.types.daysFormat, { days: item.durationDays }) : $t($ts.giftCardManagement.types.noExpiration) }}
          </template>

          <template #item.isActive="{ item }">
            <v-chip
              :color="item.isActive ? 'success' : 'error'"
              small
            >
              {{ item.isActive ? $t($ts.giftCardManagement.types.active) : $t($ts.giftCardManagement.types.inactive) }}
            </v-chip>
          </template>

          <template #item.actions="{ item }">
            <v-icon
              small
              class="mr-2"
              @click="startEdit(item)"
            >
              mdi-pencil
            </v-icon>
          </template>
        </v-data-table>
      </v-card-text>
    </v-card>
  </div>
</template>

<style scoped></style>
